/**
 * Wire format for the Proxmox serial console, as implemented by Proxmox's own
 * xterm.js client.
 *
 *   input   0:<utf8ByteLength>:<data>
 *   resize  1:<cols>:<rows>:
 *   ping    2
 *
 * The server sends raw terminal bytes back with no framing of any kind.
 * Authentication is handled server-side before the stream reaches the browser.
 */

/**
 * Proxmox rejects WebSocket frames larger than 128KB and drops the session, so
 * a paste is split into pieces that each stay comfortably under that as a
 * complete command.
 */
export const MAX_CHUNK_CODE_UNITS = 8192;

const encoder = new TextEncoder();

/**
 * Encodes one keystroke or paste.
 * The length field counts UTF-8 bytes, not JavaScript string length - they
 * differ for any non-ASCII input and a wrong count desynchronises the stream.
 */
export function encodeTerminalInput(data: string): string {
    return `0:${encoder.encode(data).length}:${data}`;
}

export function encodeTerminalResize(cols: number, rows: number): string {
    return `1:${cols}:${rows}:`;
}

/**
 * Splits input so each piece can be sent as its own command, never cutting a
 * surrogate pair in half - half a pair would be encoded as a replacement
 * character and corrupt the paste.
 */
export function chunkTerminalInput(data: string, maxCodeUnits = MAX_CHUNK_CODE_UNITS): string[] {
    if (data.length <= maxCodeUnits) return [data];

    const chunks: string[] = [];
    let start = 0;

    while (start < data.length) {
        let end = Math.min(start + maxCodeUnits, data.length);
        const code = data.charCodeAt(end - 1);
        const isHighSurrogate = code >= 0xd800 && code <= 0xdbff;
        if (isHighSurrogate && end < data.length) end -= 1;
        chunks.push(data.slice(start, end));
        start = end;
    }

    return chunks;
}
